import {
  Grid,
  Typography,
  Divider,
  IconButton,
  Dialog,
  DialogContent,
  Paper,
  Box,
  Chip,
  CircularProgress,
} from "@mui/material";
import React, { Fragment } from "react";
import { useQuery } from "@tanstack/react-query";
import HttpService from "../services/HttpService";
import Labels from "../labels/Labels";
import PaidIcon from "@mui/icons-material/Paid";
import BusinessIcon from "@mui/icons-material/Business";
import NumbersIcon from "@mui/icons-material/Numbers";
import ArticleIcon from "@mui/icons-material/Article";
import VisibilityIcon from "@mui/icons-material/Visibility";
import CloseIcon from "@mui/icons-material/Close";
import AssessmentIcon from "@mui/icons-material/Assessment";
import EmailIcon from "@mui/icons-material/Email";
import MarkEmailReadIcon from "@mui/icons-material/MarkEmailRead";
import ErrorIcon from "@mui/icons-material/Error";
import MouseIcon from "@mui/icons-material/Mouse";
import TimelineIcon from "@mui/icons-material/Timeline";

import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import CampaignEventDetail from "./CampaignEventDetail";

export default function CampaignFileDetail(props) {
  const { campaignInfo } = props;
  const [campaignSelectedItem, setCampaignSelectedItem] = React.useState();
  const [showEventsDetail, setShowEventsDetail] = React.useState(false);

  // Helper function to get color based on status
  const getStatusColor = (status) => {
    switch (status.toUpperCase()) {
      case "DELIVERED":
        return "#4CAF50"; // Green
      case "OPENED":
        return "#2196F3"; // Blue
      case "CLICKED":
        return "#4f7b77ff"; // Teal

      case "DEFERRED":
        return "#FF9800"; // Orange
      case "BOUNCED":
        return "#F44336"; // Red
      case "DROPPED":
        return "#E91E63"; // Pink
      case "SPAM":
        return "#d48484ff"; // Purple
      default:
        return "#757575"; // Grey
    }
  };

  // Helper function to get icon based on status
  const getStatusIcon = (status) => {
    switch (status.toUpperCase()) {
      case "DELIVERED":
        return <EmailIcon fontSize="small" />;
      case "OPENED":
        return <MarkEmailReadIcon fontSize="small" />;
      case "CLICKED":
        return <MouseIcon fontSize="small" />;
      case "DEFERRED":
      case "BOUNCED":
      case "DROPPED":
      case "SPAM":
        return <ErrorIcon fontSize="small" />;
      default:
        return <AssessmentIcon fontSize="small" />;
    }
  };

  const columns = [
    {
      field: "id",
      headerName: Labels.getLabel("campaignFileDetail.column.id", "ID"),
      width: 90,
      hidden: true,
    },
    {
      field: "lineNumber",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.lineNumber",
        "Line#"
      ),
      width: 50,
    },
    {
      field: "email",
      headerName: Labels.getLabel("campaignFileDetail.column.email", "Email"),
      width: 180,
    },
    {
      field: "firstName",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.firstName",
        "First name"
      ),
      width: 130,
    },
    {
      field: "lastName",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.lastName",
        "Last name"
      ),
      width: 130,
    },
    {
      field: "fullName",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.fullName",
        "Full name"
      ),
      width: 130,
    },
    {
      field: "phone",
      headerName: Labels.getLabel("campaignFileDetail.column.phone", "Phone"),
      width: 130,
    },
    {
      field: "status",
      headerName: Labels.getLabel("campaignFileDetail.column.status", "Status"),
      width: 140,
    },
    {
      field: "updateDate",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.updateDate",
        "Update Date"
      ),
      width: 150,
    },
    {
      field: "error",
      headerName: Labels.getLabel("campaignFileDetail.column.error", "Error"),
      width: 100,
    },
    {
      field: "idDescriptor",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.idDescriptor",
        "Id Descriptor"
      ),
      width: 120,
    },
    {
      field: "idCampaign",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.idCampaign",
        "Id Campaign"
      ),
      width: 120,
      hidden: true,
    },
    {
      field: "viewDetails",
      headerName: Labels.getLabel(
        "campaignFileDetail.column.viewEvents",
        "View Events"
      ),
      width: 120,
      renderCell: (params) => (
        <div key={"action." + params.id}>
          <IconButton
            key={"view." + params.id}
            onClick={() => {
              setCampaignSelectedItem({ ...params.row });
              setShowEventsDetail(true);
            }}
          >
            <VisibilityIcon />
          </IconButton>
        </div>
      ),
    },
  ];

  const EventsDetailDialog = (event) => {
    return (
      <Dialog
        open={showEventsDetail}
        fullWidth
        onClose={() => {}}
        maxWidth="xl"
        style={{ margin: "5rem", padding: "8rem" }}
      >
        <Box
          sx={{
            backgroundColor: "primary.main",
            px: 3,
            minHeight: 52,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <TimelineIcon sx={{ color: "white" }} />
            <Typography variant="h6" sx={{ color: "white" }}>
              Events in this campaign for the user: (
              {campaignSelectedItem?.email})
            </Typography>
          </Box>
          <IconButton
            onClick={() => {
              setShowEventsDetail(false);
              setCampaignSelectedItem(null);
            }}
            sx={{ color: "white" }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
        <DialogContent sx={{ paddingTop: 3 }}>
          <CampaignEventDetail campaignSelectedItem={campaignSelectedItem} />
        </DialogContent>
      </Dialog>
    );
  };

  async function fetchCampaignInfoDetail() {
    if (campaignInfo.id === undefined) return null;
    var url = window.API_URL + `/campaigns/` + campaignInfo.id;
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  async function fetchCampaignStats() {
    if (campaignInfo.id === undefined) return null;
    var url = window.API_URL + `/campaigns/` + campaignInfo.id + `/stats`;
    const response = await HttpService.getAxiosClient().get(url);
    return response;
  }

  const { data, isError, isLoading } = useQuery(
    ["campaignFileDetail", campaignInfo.id],
    fetchCampaignInfoDetail
  );

  const {
    data: statsData,
    isError: statsIsError,
    isLoading: statsIsLoading,
  } = useQuery(["campaignStats", campaignInfo.id], fetchCampaignStats);

  return (
    <Fragment>
      {isLoading ? (
        <h4>{Labels.getLabel("campaignFileDetail.Loading", "Loading...")}</h4>
      ) : !isError ? (
        <Fragment>
          <Grid
            style={{ marginLeft: "20px", marginRight: "10px", marginTop: 0 }}
            container
            spacing={1.5}
          >
            <Grid container spacing={1.5}>
              <Grid item xs={1.5}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {Labels.getLabel("campaignFileDetail.Company", "Company:")}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <BusinessIcon
                    style={{ marginRight: "5px", fontSize: "1rem" }}
                  />
                  {campaignInfo.employerName}
                </Typography>
              </Grid>
              <Grid item xs={1.5}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {Labels.getLabel("campaignFileDetail.Filename", "Filename:")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <ArticleIcon
                    style={{ marginRight: "5px", fontSize: "1rem" }}
                  />
                  {campaignInfo && campaignInfo.filename}
                </Typography>
              </Grid>
              <Grid item xs={1.5}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {Labels.getLabel("campaignFileDetail.Status", "Status:")}
                </Typography>
              </Grid>
              <Grid item xs={3}>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <PaidIcon style={{ marginRight: "5px", fontSize: "1rem" }} />
                  {campaignInfo && campaignInfo.processStatus}
                </Typography>
              </Grid>
              <Grid item xs={1.5}>
                <Typography
                  variant="body2"
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "flex-end",
                  }}
                >
                  {Labels.getLabel("campaignFileDetail.Records", "Records:")}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography
                  variant="body2"
                  sx={{ display: "flex", alignItems: "center" }}
                >
                  <NumbersIcon
                    style={{ marginRight: "5px", fontSize: "1rem" }}
                  />
                  {campaignInfo && campaignInfo.numOfRecords}
                </Typography>
              </Grid>
            </Grid>

            {/* Campaign Stats Section */}
            {statsIsLoading ? (
              <Grid
                container
                spacing={1.5}
                sx={{ mt: 2, mb: 2, justifyContent: "center" }}
              >
                <Grid item>
                  <CircularProgress />
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    {Labels.getLabel(
                      "campaignFileDetail.LoadingStats",
                      "Loading campaign statistics..."
                    )}
                  </Typography>
                </Grid>
              </Grid>
            ) : statsIsError ? (
              <Grid container spacing={1.5} sx={{ mt: 2, mb: 2 }}>
                <Grid item xs={12}>
                  <Typography color="error">
                    {Labels.getLabel(
                      "campaignFileDetail.ErrorStats",
                      "Error loading campaign statistics."
                    )}
                  </Typography>
                </Grid>
              </Grid>
            ) : (
              statsData?.data?.stats && (
                <Grid
                  container
                  spacing={1}
                  sx={{
                    mt: 1,
                    mb: 1,
                    justifyContent: "center",
                  }}
                >
                  <Grid item xs={12}>
                    <Typography
                      variant="caption"
                      sx={{ mb: 0.25, display: "flex", alignItems: "center" }}
                    >
                      <AssessmentIcon
                        style={{
                          paddingRight: "3px",
                          fontSize: "0.8rem",
                        }}
                      />
                      {Labels.getLabel(
                        "campaignFileDetail.CampaignStatistics",
                        "Campaign Statistics"
                      )}
                    </Typography>
                  </Grid>

                  {statsData.data.stats
                    .sort((a, b) => {
                      const order = [
                        "CLICKED",
                        "OPENED",
                        "DELIVERED",
                        "DEFERRED",
                        "BOUNCED",
                        "DROPPED",
                        "SPAM",
                      ];
                      return (
                        order.indexOf(a.status.toUpperCase()) -
                        order.indexOf(b.status.toUpperCase())
                      );
                    })
                    .map((stat) => (
                      <Grid
                        item
                        xs={4}
                        sm={3}
                        md={2}
                        lg={1.5}
                        key={stat.status}
                        sx={{ display: "flex", justifyContent: "center" }}
                      >
                        <Paper
                          elevation={1}
                          sx={{
                            p: 0.5,
                            textAlign: "center",
                            backgroundColor: getStatusColor(stat.status),
                            color: "white",
                            borderRadius: 0.5,
                            minWidth: "48px",
                            width: "60%",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              flexDirection: "row",
                              alignItems: "center",
                              width: "100%",
                              gap: 0.75,
                            }}
                          >
                            {getStatusIcon(stat.status)}
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography
                                variant="caption"
                                sx={{
                                  fontSize: "0.7rem",
                                  fontWeight: "bold",
                                  display: "block",
                                  textAlign: "center",
                                }}
                              >
                                {stat.status}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: "bold",
                                  display: "block",
                                  textAlign: "center",
                                }}
                              >
                                {stat.count}
                              </Typography>
                            </Box>
                          </Box>
                        </Paper>
                      </Grid>
                    ))}
                </Grid>
              )
            )}

            <Divider style={{ margin: "15px" }} />
          </Grid>
          <div style={{ height: 550 }}>
            <DataGrid
              columnVisibilityModel={{ id: false }}
              disableSelectionOnClick
              disableColumnFilter
              disableColumnSelector
              disableDensitySelector
              rows={data?.data}
              columns={columns}
              getRowId={(row) => row.lineNumber}
              getRowHeight={() => 35}
              density="compact"
              pageSize={10}
              rowsPerPageOptions={[10]}
              components={{ Toolbar: GridToolbar }}
              componentsProps={{
                toolbar: {
                  showQuickFilter: true,
                  quickFilterProps: { debounceMs: 500 },
                },
              }}
            />
          </div>
          {showEventsDetail ? EventsDetailDialog() : ""}
        </Fragment>
      ) : (
        <h4>
          {Labels.getLabel(
            "campaignFileDetail.Error",
            "An error ocurred, please try again later..."
          )}
        </h4>
      )}
    </Fragment>
  );
}
